import * as React from "react";

import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { Button } from "@repo/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/card";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@repo/ui/components/sidebar";
import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Loader2, ShieldAlert } from "lucide-react";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";

import { AppSidebar } from "@/components/dashboard/app-sidebar";
import { authClient, ClientAuthBoundary, convexAuthClient } from "@/lib/auth-client";
import { Breadcrumbs } from "@/lib/breadcrumb-config";
import { authConvexClient } from "@/lib/convex-clients";
import { canAccessPath, defaultPathForRole, useRoleAccess } from "@/lib/role-access";

export const Route = createFileRoute("/_authed")({
  component: () => (
    <ConvexBetterAuthProvider client={authConvexClient} authClient={convexAuthClient}>
      <ClientAuthBoundary>
        <NuqsAdapter>
          <AuthedLayout />
        </NuqsAdapter>
      </ClientAuthBoundary>
    </ConvexBetterAuthProvider>
  ),
});

function AuthedLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { auth, isPending, role } = useRoleAccess();

  const needsRedirect =
    !auth.isPending &&
    !auth.isRefetching &&
    auth.data &&
    !isPending &&
    !canAccessPath(role, pathname);

  const fallbackPath = defaultPathForRole(role);

  React.useEffect(() => {
    if (needsRedirect && fallbackPath) {
      void navigate({ replace: true, to: fallbackPath });
    }
  }, [fallbackPath, navigate, needsRedirect]);

  if (auth.isPending) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isPending) {
    return <PendingRoleScreen />;
  }

  if (needsRedirect) {
    return null;
  }

  return (
    <SidebarProvider className="h-svh w-full overflow-hidden">
      <AppSidebar />
      <SidebarInset className="min-h-0">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Breadcrumbs pathname={pathname} />
        </header>
        <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-0">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

function PendingRoleScreen() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="mb-2 flex items-center gap-2 text-muted-foreground">
            <ShieldAlert className="size-4" />
            <span className="text-sm font-medium">Access pending</span>
          </div>
          <CardTitle>Twoje konto czeka na aktywację</CardTitle>
          <CardDescription>
            Musisz poczekać, aż admin przypisze Ci odpowiednią rolę w aplikacji.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            Gdy admin zaktualizuje Twoją rolę, uzyskasz dostęp do odpowiednich widoków.
          </p>
          <div>
            <Button
              onClick={async () => {
                await authClient.signOut();
                window.location.assign("/login");
              }}
              variant="outline"
            >
              Wyloguj się
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
